import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject } from '@angular/core';

import {
    ArticleVatRate,
    ArticleVatRateType,
    FiscalDeviceBO, PaymentMethodItem, PaymentMethodQuery, SuzInternalResponse, SuzInternalStatus,
    SuzService,
    VatRateForSaleBaseQuery,
    VatRateItem
} from '@skylab-shared/business';
import { IdentifiableValueConverter } from '@skylab-shared/core';
import {
    FiscalDeviceModel,
    FiscalDevicePaymentMethodsParametersMappingModel,
    FiscalDevicePaymentMethodsParametersModel,
    FiscalDeviceVatRatesParametersMappingModel, FiscalDeviceVatRatesParametersModel
} from '@skylab-shared/dataModel';
import { isFunction } from '@skylab-shared/utils';

import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CommonColors } from '@inscomponents/core';
import { FiscalDeviceRepository, FiscalDeviceRepositoryBase, SUZ_SERVICE } from '@skylab-angular-shared/business';
import {
    CommandBus,
    EventBus,
    EventBusBase, L10n,
    L10nBase,
    RootContext,
    RootContextBase, Weaver,
    WeaverBase
} from '@skylab-angular-shared/core';
import { TranslatePipe } from '@skylab-angular-shared/pipes';
import {
    BooleanValueAccessorDirective,
    ButtonComponent,
    DialogContentComponentIns,
    DialogHeaderComponentIns,
    DialogInsService, FocusByDirective, FormContainerComponent, GridInsComponent, GridTemplateDirective, SelectValueAccessorDirective, ServiceGridColumn, ServiceGridOptions, ToastService,
    TooltipDirective,
    ValidateDirective,
    ValidationSourceDirective,
    IconComponent
} from '@skylab-angular-shared/ui';
import { BehaviorSubject } from 'rxjs';
import {
    DictionaryDialogComponentBase, DictionaryDialogData, DictionaryDialogViewModelBase
} from '../dictionary-dialog-base';
import {
    FiscalDevicePaymentMethodsParametersMappingModelWrapper, FiscalDeviceVatRatesParametersMappingModelWrapper
} from './fiscal-device-dialog-grid-item';
import { FiscalDeviceDialogGridMappedPaymentMethod } from './grid/fiscal-device-dialog-payment-method-direct-grid/fiscal-device-dialog-grid-mapped-payment-method-cell.component';
import { FiscalDeviceDialogPaymentMethodDirectGridColumnIdentitiy, FiscalDeviceDialogPaymentMethodDirectGridCustomTemplates, FiscalDeviceDialogPaymentMethodDirectGridService } from './grid/fiscal-device-dialog-payment-method-direct-grid/fiscal-device-dialog-payment-method-direct-grid.service';
import { FiscalDeviceDialogGridPaymentMethodId } from './grid/fiscal-device-dialog-payment-method-grid/fiscal-device-dialog-grid-payment-method-id-cell.component';
import { FiscalDeviceDialogPaymentMethodGridColumnIdentitiy, FiscalDeviceDialogPaymentMethodGridCustomTemplates, FiscalDeviceDialogPaymentMethodGridService } from './grid/fiscal-device-dialog-payment-method-grid/fiscal-device-dialog-payment-method-grid.service';
import { FiscalDeviceDialogGridTechnicalZeroComponent } from './grid/fiscal-device-dialog-vat-rates-grid/fiscal-device-dialog-grid-technical-zero-cell.component';
import { FiscalDeviceDialogGridVatRateId } from './grid/fiscal-device-dialog-vat-rates-grid/fiscal-device-dialog-grid-vat-rate-id-cell.component';
import { FiscalDeviceDialogVatRatesGridColumnIdentitiy, FiscalDeviceDialogVatRatesGridCustomTemplates, FiscalDeviceDialogVatRatesGridService } from './grid/fiscal-device-dialog-vat-rates-grid/fiscal-device-dialog-vat-rates-grid.service';

interface FiscalDeviceViewModel extends DictionaryDialogViewModelBase<FiscalDeviceBO> {
    deviceName: {
        value: string;
    };

    vatRatesGridOptions: ServiceGridOptions<FiscalDeviceVatRatesParametersMappingModelWrapper, FiscalDeviceDialogVatRatesGridColumnIdentitiy, unknown>;
    vatRatesGridColumns$: BehaviorSubject<ServiceGridColumn<FiscalDeviceDialogVatRatesGridColumnIdentitiy, unknown, FiscalDeviceVatRatesParametersMappingModelWrapper, unknown, FiscalDeviceDialogVatRatesGridCustomTemplates>[]>;
    vatRatesGridDataSource$: BehaviorSubject<FiscalDeviceVatRatesParametersMappingModelWrapper[]>;

    vatRatesLoading: boolean;
    vatRateVisible: boolean;
    resetVatRatesParameters(): void;
    loadVatRatesAsync(): Promise<void>;

    paymentMethodDirectMappings: boolean;
    paymentMethodsLoading: boolean;

    paymentMethodDirectGridOptions: ServiceGridOptions<FiscalDevicePaymentMethodsParametersMappingModelWrapper, FiscalDeviceDialogPaymentMethodDirectGridColumnIdentitiy, unknown>;
    paymentMethodDirectGridColumns$: BehaviorSubject<ServiceGridColumn<FiscalDeviceDialogPaymentMethodDirectGridColumnIdentitiy, unknown, FiscalDevicePaymentMethodsParametersMappingModelWrapper, unknown, FiscalDeviceDialogPaymentMethodDirectGridCustomTemplates>[]>;
    paymentMethodDirectGridDataSource$: BehaviorSubject<FiscalDevicePaymentMethodsParametersMappingModelWrapper[]>;
    paymentMethodDirectVisible: boolean;

    paymentMethodGridOptions: ServiceGridOptions<FiscalDevicePaymentMethodsParametersMappingModelWrapper, FiscalDeviceDialogPaymentMethodGridColumnIdentitiy, unknown>;
    paymentMethodGridColumns$: BehaviorSubject<ServiceGridColumn<FiscalDeviceDialogPaymentMethodGridColumnIdentitiy, unknown, FiscalDevicePaymentMethodsParametersMappingModelWrapper, unknown, FiscalDeviceDialogPaymentMethodGridCustomTemplates>[]>;
    paymentMethodGridDataSource$: BehaviorSubject<FiscalDevicePaymentMethodsParametersMappingModelWrapper[]>;
    paymentMethodVisible: boolean;
    resetPaymentMethodsParameters(): void;
    loadPaymentMethodsAsync(): Promise<void>;

    devices: string[];
    devicesListError: string | null;
    deviceConnectionStatusViewData: {
        tooltipText: string;
        classNames: string[],
        tooltipType: CommonColors
    } | null;
    deviceInfoLoading: boolean;
    refreshConnectionAsync(): Promise<void>;
}

interface FiscalDeviceDialogData extends DictionaryDialogData {
    isDefault: boolean;
}

@Component({
    selector: 'skl-fiscal-device-dialog',
    templateUrl: './fiscal-device-dialog.component.html',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [DialogContentComponentIns, DialogHeaderComponentIns, FormContainerComponent, ValidationSourceDirective, BooleanValueAccessorDirective, FormsModule, ValidateDirective, SelectValueAccessorDirective, FocusByDirective, NgClass, TooltipDirective, ButtonComponent, GridInsComponent, GridTemplateDirective, FiscalDeviceDialogGridVatRateId, FiscalDeviceDialogGridTechnicalZeroComponent, FiscalDeviceDialogGridPaymentMethodId, FiscalDeviceDialogGridMappedPaymentMethod, ButtonComponent, TranslatePipe, IconComponent]
})
export class FiscalDeviceDialogComponent extends DictionaryDialogComponentBase<
    FiscalDeviceModel,
    FiscalDeviceBO,
    FiscalDeviceRepositoryBase,
    FiscalDeviceViewModel,
    FiscalDeviceDialogData
> {
    private vatRates!: VatRateItem[];
    private paymentMethods!: PaymentMethodItem[];
    public constructor(
        @Inject(FiscalDeviceRepository) fiscalDeviceRepositoryBase: FiscalDeviceRepositoryBase,
        @Inject(L10n) l10n: L10nBase,
        @Inject(Weaver) weaver: WeaverBase,
        @Inject(RootContext) rootContext: RootContextBase,
        @Inject(EventBus) eventBus: EventBusBase,
        @Inject(SUZ_SERVICE) private readonly suzService: SuzService,
        private readonly fiscalDeviceDialogVatRatesGridService: FiscalDeviceDialogVatRatesGridService,
        private readonly fiscalDeviceDialogPaymentMethodGridService: FiscalDeviceDialogPaymentMethodGridService,
        private readonly fiscalDeviceDialogPaymentMethodDirectGridService: FiscalDeviceDialogPaymentMethodDirectGridService,
        dialogService: DialogInsService,
        toastService: ToastService,
        commandBus: CommandBus
    ) {
        super(
            fiscalDeviceRepositoryBase,
            toastService,
            l10n,
            weaver,
            rootContext,
            commandBus,
            eventBus,
            dialogService
        );
    }
    // #region prepareration

    protected override getInitialState(): FiscalDeviceViewModel {
        const self = this;

        return {
            ...super.getInitialState(),

            deviceName: {
                get value(): string {
                    return self.viewModel.bo.model.name;
                },
                set value(value: string) {
                    self.viewModel.bo.model.name = value;
                    if (value !== null) {
                        (async () => {
                            await self.refreshDeviceConnectionAsync();
                            self.resetPaymentMethodsParameters();
                            self.resetVatRatesParameters();
                        })().catch(err => self.logger.error(err));
                    }
                },
            },

            devices: [],
            deviceInfoLoading: false,
            devicesListError: null,
            deviceConnectionStatusViewData: null,
            refreshConnectionAsync: async () => {
                const response = await this.refreshDevicesListAsync();
                if (!response) return;
                if (response.type === SuzInternalStatus.Success && response.data) {
                    this.viewModel.devices = response.data;
                    this.viewModel.devicesListError = null;
                    await this.refreshDeviceConnectionAsync();
                } else {
                    this.viewModel.deviceConnectionStatusViewData = null;
                    this.viewModel.devicesListError = this.l10n.instant('scenes.configuration.shared.fiscalization.dialog.messages.printers-names-error');
                }
            },
            vatRatesGridOptions: {
                selection: null,
                viewType: 'table-virtual',
                virtualizationConfig: {
                    maxRows: 10,
                    rowHeight: 50,
                    scrollToBottomAfterDataChanged: true
                }
            },
            vatRatesGridDataSource$: new BehaviorSubject<FiscalDeviceVatRatesParametersMappingModelWrapper[]>([]),
            vatRatesGridColumns$: new BehaviorSubject(this.fiscalDeviceDialogVatRatesGridService.getGridColumns()),
            vatRatesLoading: false,
            vatRateVisible: false,
            loadVatRatesAsync: this.loadVatRatesAsync.bind(this),
            resetVatRatesParameters: this.resetVatRatesParameters.bind(this),

            paymentMethodGridOptions: {
                selection: null,
                viewType: 'table-virtual',
                virtualizationConfig: {
                    maxRows: 10,
                    rowHeight: 50,
                    scrollToBottomAfterDataChanged: true
                }
            },
            paymentMethodGridDataSource$: new BehaviorSubject<FiscalDevicePaymentMethodsParametersMappingModelWrapper[]>([]),
            paymentMethodGridColumns$: new BehaviorSubject(this.fiscalDeviceDialogPaymentMethodGridService.getGridColumns()),

            paymentMethodsLoading: false,
            paymentMethodVisible: false,

            paymentMethodDirectGridOptions: {
                selection: null,
                viewType: 'table-virtual',
                virtualizationConfig: {
                    maxRows: 10,
                    rowHeight: 50,
                    scrollToBottomAfterDataChanged: true
                }
            },
            paymentMethodDirectGridDataSource$: new BehaviorSubject<FiscalDevicePaymentMethodsParametersMappingModelWrapper[]>([]),
            paymentMethodDirectGridColumns$: new BehaviorSubject(this.fiscalDeviceDialogPaymentMethodDirectGridService.getGridColumns()),

            loadPaymentMethodsAsync: this.loadPaymentMethodsAsync.bind(this),
            resetPaymentMethodsParameters: this.resetPaymentMethodsParameters.bind(this),
        };
    }

    protected override async preloadDataAsync(): Promise<void> {
        await Promise.all([
            this.localContext.preload(PaymentMethodQuery),
            this.localContext.preload(VatRateForSaleBaseQuery),
        ]);
    }

    protected override async prepareViewModelAsync(): Promise<void> {
        await super.prepareViewModelAsync();
        this.paymentMethods = this.localContext.getPreloaded(PaymentMethodQuery) || [];
        this.vatRates = (this.localContext.getPreloaded(VatRateForSaleBaseQuery) || []).sort((a, b) => {
            if (a.rate > b.rate) {
                return -1;
            }
            if (a.rate < b.rate) {
                return 1;
            }
            if (a.rate === b.rate) {
                if (!a.untaxed) {
                    return -1;
                }
                return 1;
            }
            return 0;
        });

        if (this.data.isDefault || this.viewModel.bo.model.default) {
            this.viewModel.bo.model.default = true;
        }

        await this.viewModel.refreshConnectionAsync();
        await this.loadVatRatesAsync();
    }

    // #endregion

    //#region vat rates
    private async loadVatRatesAsync(): Promise<void> {
        const getVATRate = (deviceVATRate: ArticleVatRate): VatRateItem => {
            const dummyVatRate = { id: null, symbol: null } as unknown as VatRateItem;
            switch (deviceVATRate.VatRateType) {
                case ArticleVatRateType.Numeric:
                    const numericVatRates = this.vatRates.filter(v => v.rate === deviceVATRate.Value);
                    if (deviceVATRate.Value === 0) {
                        const zeroVatRate = numericVatRates.find(v => !v.untaxed && !v.exempt);
                        return zeroVatRate ? zeroVatRate : dummyVatRate;
                    } else {
                        const vatRate = numericVatRates.shift();
                        return vatRate ? vatRate : dummyVatRate;
                    }
                case ArticleVatRateType.Inactive:
                    const unTaxedVatRate = this.vatRates.find(v => v.exempt);
                    return unTaxedVatRate ? unTaxedVatRate : dummyVatRate;

                case ArticleVatRateType.Exempt:
                    const exepmtVatRate = this.vatRates.find(v => v.exempt);
                    return exepmtVatRate ? exepmtVatRate : dummyVatRate;
                default:
                    return dummyVatRate;
            }
        };

        const vatRateExists = (id: number): boolean => {
            return this.vatRates.some(v => v.id === id);
        };

        if (!this.suzLoading) {
            if (this.viewModel.bo.model.name) {
                this.viewModel.vatRatesLoading = true;
                this.notifySuzLoadingChange();

                await this.suzService.getPrinterVatTableAsync(this.viewModel.bo.model.name).then(
                    response => {
                        if (response) {
                            const modelWrapperMappings = [];

                            if (!this.viewModel.bo.model.vatRatesParameters) {
                                this.viewModel.bo.model.vatRatesParameters = new FiscalDeviceVatRatesParametersModel();
                            }

                            // even saved mappings base on the available mappings on physical device
                            // clear model mappings and then fill collection with proper adjustment to physical device mappings
                            const savedMappings: FiscalDeviceVatRatesParametersMappingModel[] = [];
                            for (let i = this.viewModel.bo.model.vatRatesParameters.mappings.length - 1; i >= 0; i--) {
                                savedMappings.unshift(this.viewModel.bo.model.vatRatesParameters.mappings.removeAt(i));
                            }

                            for (const key in response) {
                                if (response[key]) {
                                    const vatRateBasedOnFiscalDevice = getVATRate(response[key]);
                                    const physicalVatRateSymbol =  vatRateBasedOnFiscalDevice.symbol
                                        ? vatRateBasedOnFiscalDevice.symbol
                                        : response[key].Value! * 100 + '%';

                                    let mappingModel = savedMappings.find(m => m.mapped === key);
                                    if (!mappingModel) {
                                        mappingModel = new FiscalDeviceVatRatesParametersMappingModel();
                                        this.viewModel.bo.model.vatRatesParameters.mappings.push(mappingModel);

                                        mappingModel.vatRateId = (vatRateBasedOnFiscalDevice.id !== null && vatRateExists(vatRateBasedOnFiscalDevice.id)) ? vatRateBasedOnFiscalDevice.id : null as any;
                                        mappingModel.mapped = key;
                                    } else {
                                        this.viewModel.bo.model.vatRatesParameters.mappings.push(mappingModel);
                                    }

                                    // always assing physical vat rate value to mapping model value
                                    mappingModel.value = response[key].Value!;
                                    // due to outside system changes on device

                                    modelWrapperMappings.push(
                                        new FiscalDeviceVatRatesParametersMappingModelWrapper(
                                            mappingModel,
                                            this.viewModel.bo.model.vatRatesParameters,
                                            physicalVatRateSymbol,
                                            this.vatRates,
                                            new IdentifiableValueConverter(this.vatRates),
                                        ),
                                    );
                                }
                            }

                            this.viewModel.vatRatesGridDataSource$.next(modelWrapperMappings);
                            this.viewModel.vatRateVisible = true;
                        }
                        this.viewModel.vatRatesLoading = false;
                        this.notifySuzLoadingChange();
                    },
                    () => {
                        this.viewModel.vatRatesLoading = false;
                        this.notifySuzLoadingChange();
                    },
                );
            }
        } else {
            const handler = this.onSuzLoadingChange(newValue => {
                if (!newValue) {
                    handler();
                    this.loadVatRatesAsync();
                }
            });
            this.registerListening(handler);
        }
    }

    private resetVatRatesParameters() {
        this.viewModel.vatRatesGridDataSource$.next([]);

        if (this.viewModel.bo.model.vatRatesParameters?.mappings) {
            this.viewModel.bo.model.vatRatesParameters.mappings.clear();
            this.viewModel.bo.model.vatRatesParameters = null;
        }
        this.viewModel.vatRateVisible = false;
    }
    //#endregion

    //#region payment method
    private async loadPaymentMethodsAsync(): Promise<void> {
        if (!this.suzLoading) {
            if (this.viewModel.bo.model.name) {
                this.viewModel.paymentMethodsLoading = true;
                this.notifySuzLoadingChange();

                await this.suzService.getPrinterPaymentMethodsAsync(this.viewModel.bo.model.name).then(
                    response => {
                        if (response?.type === SuzInternalStatus.OperationNotExecuted) {
                            // when physical device mappings doesnt exist

                            if (!this.viewModel.bo.model.paymentMethodsParameters) {
                                this.viewModel.bo.model.paymentMethodsParameters = new FiscalDevicePaymentMethodsParametersModel();

                                this.viewModel.paymentMethodDirectGridDataSource$.next(
                                    this.paymentMethods.map((paymentMethod: PaymentMethodItem) => {
                                        const mappingModel = new FiscalDevicePaymentMethodsParametersMappingModel();
                                        this.viewModel.bo.model.paymentMethodsParameters?.mappings.push(mappingModel);
                                        mappingModel.mapped = paymentMethod.name;
                                        mappingModel.paymentMethodId = paymentMethod.id;
                                        return new FiscalDevicePaymentMethodsParametersMappingModelWrapper(
                                            mappingModel,
                                            this.paymentMethods,
                                            new IdentifiableValueConverter(this.paymentMethods),
                                        );
                                    }),
                                );
                            } else {
                                this.viewModel.paymentMethodDirectGridDataSource$.next(
                                    this.viewModel.bo.model.paymentMethodsParameters.mappings.map(
                                        (m: FiscalDevicePaymentMethodsParametersMappingModel) =>
                                            new FiscalDevicePaymentMethodsParametersMappingModelWrapper(
                                                m,
                                                this.paymentMethods,
                                                new IdentifiableValueConverter(this.paymentMethods),
                                            ),
                                    ),
                                );
                            }
                            this.viewModel.paymentMethodDirectVisible = true;
                        } else if (response?.type === SuzInternalStatus.Success) {
                            // when device mappings exists
                            const modelWrapperMappings = [];

                            if (!this.viewModel.bo.model.paymentMethodsParameters) {
                                this.viewModel.bo.model.paymentMethodsParameters = new FiscalDevicePaymentMethodsParametersModel();
                            }

                            // even saved mappings base on the available mappings on physical device
                            // clear model mappings and then fill collection with proper adjustment to physical device mappings
                            const savedMappings: FiscalDevicePaymentMethodsParametersMappingModel[] = [];
                            for (
                                let i = this.viewModel.bo.model.paymentMethodsParameters.mappings.length - 1;
                                i >= 0;
                                i--
                            ) {
                                savedMappings.unshift(
                                    this.viewModel.bo.model.paymentMethodsParameters.mappings.removeAt(i),
                                );
                            }

                            const data = response.data;
                            if (data) {
                                for (const key in data) {
                                    if (data[key]) {
                                        let mappingModel = savedMappings.find(m => m.mapped === data[key]);
                                        if (!mappingModel) {
                                            mappingModel = new FiscalDevicePaymentMethodsParametersMappingModel();
                                            this.viewModel.bo.model.paymentMethodsParameters.mappings.push(mappingModel);
                                            mappingModel.paymentMethodId = null as any;
                                            mappingModel.mapped = data[key];
                                        } else {
                                            this.viewModel.bo.model.paymentMethodsParameters.mappings.push(mappingModel);
                                        }
                                        modelWrapperMappings.push(
                                            new FiscalDevicePaymentMethodsParametersMappingModelWrapper(
                                                mappingModel,
                                                this.paymentMethods,
                                                new IdentifiableValueConverter(this.paymentMethods),
                                            ),
                                        );
                                    }
                                }
                            }
                            this.viewModel.paymentMethodGridDataSource$.next(modelWrapperMappings);
                            this.viewModel.paymentMethodVisible = true;
                        }
                        this.viewModel.paymentMethodsLoading = false;
                        this.notifySuzLoadingChange();
                    },
                    () => {
                        this.viewModel.paymentMethodsLoading = false;
                        this.notifySuzLoadingChange();
                    },
                );
            }
        } else {
            const handler = this.onSuzLoadingChange(newValue => {
                if (!newValue) {
                    handler();
                    this.loadPaymentMethodsAsync();
                }
            });
            this.registerListening(handler);
        }
    }

    private resetPaymentMethodsParameters() {
        this.viewModel.paymentMethodGridDataSource$.next([]);

        if (this.viewModel.bo.model.paymentMethodsParameters?.mappings) {
            this.viewModel.bo.model.paymentMethodsParameters.mappings.clear();
            this.viewModel.bo.model.paymentMethodsParameters = null;
        }
        this.viewModel.paymentMethodVisible = false;
        this.viewModel.paymentMethodDirectVisible = false;
    }
    //#endregion

    //#region loadings

    private get suzLoading() {
        if (this.viewModel) {
            return (
                this.viewModel.vatRatesLoading ||
                this.viewModel.paymentMethodsLoading ||
                this.viewModel.deviceInfoLoading
            );
        }
        return false;
    }

    private suzLoadingHandlers: ((newValue: boolean) => void)[] = [];
    private onSuzLoadingChange(callback: (newValue: boolean) => void) {
        this.suzLoadingHandlers.push(callback);

        return () => {
            const idx = this.suzLoadingHandlers.indexOf(callback);
            if (idx === -1) return;
            this.suzLoadingHandlers.splice(idx, 1);
        };
    }
    private notifySuzLoadingChange(): void {
        this.suzLoadingHandlers.forEach((callback: (newValue: boolean) => void) => {
            if (isFunction(callback)) callback(!!this.suzLoading);
        });
    }

    private async refreshDevicesListAsync(): Promise<SuzInternalResponse<string[]> | undefined> {
        if (this.suzLoading) return;
        this.viewModel.deviceInfoLoading = true;
        const response = await this.suzService.getExistingPrintersNamesAsync();
        this.viewModel.deviceInfoLoading = false;

        return response;
    }

    private getConnectionStatuses(type: SuzInternalStatus): typeof this.viewModel.deviceConnectionStatusViewData {
        if (type === SuzInternalStatus.Success) {
            return { tooltipText: this.l10n._T('scenes.configuration.shared.fiscalization.messages.ok'), classNames: ['icon-check', 'text--success'], tooltipType: 'secondary' };
        } else if (type === SuzInternalStatus.DeviceNotExistsError) {
            return { tooltipText: this.l10n._T('scenes.configuration.shared.fiscalization.messages.lack-of-device'), classNames: ['icon-exclamation-triangle', 'text--warning'], tooltipType: 'warning' };
        } else if (type === SuzInternalStatus.DeviceConnectionError) {
            return { tooltipText: this.l10n._T('scenes.configuration.shared.fiscalization.messages.lack-of-connection'), classNames: ['icon-exclamation-triangle', 'text--warning'], tooltipType: 'warning' };
        } else {
            return { tooltipText: this.l10n._T('scenes.configuration.shared.fiscalization.messages.lack-of-connection'), classNames: ['icon-exclamation-triangle', 'text--warning'], tooltipType: 'warning' };
        }
    }

    private async refreshDeviceConnectionAsync(): Promise<void> {
        if (!this.suzLoading) {
            if (this.viewModel.bo.model.name) {
                try {
                    this.viewModel.deviceInfoLoading = true;
                    const response = await this.suzService.checkPrinterConnectionAsync(this.viewModel.bo.model.name);
                    this.viewModel.deviceConnectionStatusViewData = this.getConnectionStatuses(response.type);
                } catch (err) {
                    this.logger.error(err);
                } finally {
                    this.viewModel.deviceInfoLoading = false;
                }
            } else {
                this.viewModel.deviceConnectionStatusViewData = null;
            }
        }
    }

    //#endregion
}
